import React, { useEffect, useState, useCallback } from 'react';
import ChatWindow from './components/ChatWindow';
import GoogleLogin from './GoogleLogin';
import BugReports from './components/BugReports';
import SharedThreadView from './components/SharedThreadView';
import MeetingInsightsPage from './components/MeetingInsights/MeetingInsightsPage';
import DiscordMappings from './components/DiscordMappings';
import { RecordingProvider } from './contexts/RecordingContext';
import { DialogueProvider } from './contexts/DialogueContext';
import { AuthProvider } from './contexts/AuthContext';
import { 
  loginWithGoogle,
  logout as sessionLogout,
  isSessionValid,
  getStoredUser,
  saveSession,
  clearSession
} from './utils/sessionManager';
import './App.css';

// Detect iOS auth modal from URL param and persist in sessionStorage
const iosAuthParams = new URLSearchParams(window.location.search);
if (iosAuthParams.get('iosauthform') === '1') {
  sessionStorage.setItem('iosauthform', '1');
}
const isIosAuthModal = sessionStorage.getItem('iosauthform') === '1';

export default function App() {
  // Check for existing session on mount (including iOS auth_token callback)
  const [user, setUser] = useState(() => {
    // iOS auth modal: always show login screen, never the app
    if (isIosAuthModal) {
      clearSession();
      return null;
    }

    const params = new URLSearchParams(window.location.search);
    const iosToken = params.get('auth_token');

    // iOS callback with auth_token: will be handled by useEffect below
    // Don't try to use it directly — it's a Google idToken, not a session token
    if (iosToken && params.get('ios') === '1') {
      return null; // useEffect will exchange it for a session token
    }

    if (isSessionValid()) {
      const storedUser = getStoredUser();
      if (storedUser) {
        // Web: httpOnly cookie holds the session; no sessionToken in JS.
        // iOS: native layer owns the token; it's not read from localStorage.
        return { ...storedUser, useSession: true };
      }
    }
    return null;
  });
  // Detect iOS callback mode — prevents showing login screen while we process the token
  const [iosCallback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ios') !== '1') return false;
    // Extract auth_token from raw URL to preserve + characters, then decode
    const match = window.location.search.match(/[?&]auth_token=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : false;
  });
  const [isLoading, setIsLoading] = useState(!!iosCallback);
  const [error, setError] = useState(null);

  // Handle iOS auth_token (Google idToken) — exchange for session token
  useEffect(() => {
    if (!iosCallback || user) return;

    (async () => {
      setIsLoading(true);
      try {
        const sessionData = await loginWithGoogle(iosCallback);
        // Notify iOS app of session token via hidden iframe
        try {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = `dgshare://store-session?session_token=${sessionData.sessionToken}`;
          document.body.appendChild(iframe);
          setTimeout(() => iframe.remove(), 1000);
        } catch (e) { /* ignore if scheme not handled */ }
        window.history.replaceState({}, '', window.location.pathname);
        setUser({
          ...sessionData.user,
          sessionToken: sessionData.sessionToken,
          useSession: true,
        });
      } catch (err) {
        setError('[iOS] ' + err.message);
        window.history.replaceState({}, '', window.location.pathname);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [iosCallback, user]);

  // Boot: if localStorage doesn't already carry an active session (e.g. right
  // after an SSO callback set the httpOnly cookie but no client-side state
  // yet), ask the server who we are and hydrate from that.
  useEffect(() => {
    if (user || iosCallback || isIosAuthModal) return;
    setIsLoading(true);
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data?.user) {
          saveSession(null, data.expiresAt, data.user);
          setUser({ ...data.user, useSession: true });
        }
      })
      .catch(() => { /* not authed — fall through to login screen */ })
      .finally(() => setIsLoading(false));
    // Deliberately runs once on mount; the other paths (localStorage, iOS
    // callback) resolve `user` synchronously and skip this check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = useCallback(async (idToken, profile) => {
    // iOS auth modal: redirect to native app with the Google idToken
    if (isIosAuthModal) {
      window.location.href = `dgshare://auth-callback?auth_token=${idToken}`;
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Exchange Google ID token for session — backend sets httpOnly cookie.
      const sessionData = await loginWithGoogle(idToken);

      // On web we do NOT keep the session token in JS state (cookie handles auth).
      // iOS callback path has its own useEffect that reads sessionToken and hands
      // it to the native bridge, so it stays out of App's main login path.
      const newUser = {
        ...sessionData.user,
        useSession: true,
        email: profile.email,
        name: profile.name,
        picture: profile.picture
      };

      setUser(newUser);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
      // Fall back to using Google ID token directly
      const newUser = { idToken, ...profile, useSession: false };
      setUser(newUser);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    // Legacy Google one-tap opt-out — cheap, harmless on the SSO path.
    window.google?.accounts.id.disableAutoSelect?.();

    // iOS webview keeps the session token in native storage; a cross-domain
    // browser redirect through the IdP doesn't cleanly work there, so the
    // native bridge path keeps the old cookie-only logout.
    if (user?.sessionToken) {
      await sessionLogout();
      setUser(null);
      return;
    }

    // Web: bounce through /api/sso/logout — that endpoint tears down our
    // own session (Firestore record + sessionToken cookie) AND then
    // 302's through dgapps.ai/sso/logout so the IdP session is destroyed
    // too. Skipping that step would leave the IdP session live: clicking
    // "sign in" again would silently re-authenticate without a Google
    // prompt, defeating the point of the sign-out.
    clearSession();
    window.location.href = `/api/sso/logout?return_to=${encodeURIComponent('/')}`;
  }, [user]);

  // Check session validity periodically
  useEffect(() => {
    if (!user || !user.useSession) return;
    
    const checkInterval = setInterval(() => {
      if (!isSessionValid()) {
        console.log('Session expired, logging out...');
        handleLogout();
      }
    }, 60 * 1000); // Check every minute
    
    return () => clearInterval(checkInterval);
  }, [user, handleLogout]);

  if (isLoading) {
    return (
      <section className="centered">
        <h2>Logging in...</h2>
      </section>
    );
  }

  if (error) {
    return (
      <section className="centered">
        <h2>Login Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </section>
    );
  }

  // If we have a user (either with session or Google token), show the chat
  if (user) {
    const getAuthHeader = () => {
      // iOS native bridge still uses explicit token header.
      if (user.useSession && user.sessionToken) {
        return `Session ${user.sessionToken}`;
      }
      if (user.idToken) {
        return `Bearer ${user.idToken}`;
      }
      // Web: auth via httpOnly cookie — no header needed.
      return '';
    };

    // Simple URL-based routing
    const path = window.location.pathname;

    // Bug reports page (direct link only)
    if (path === '/bug-reports') {
      return (
        <AuthProvider getAuthHeader={getAuthHeader} user={user}>
          <BugReports />
        </AuthProvider>
      );
    }

    // Meeting Insights page
    if (path === '/meeting-insights') {
      return (
        <AuthProvider getAuthHeader={getAuthHeader} user={user}>
          <MeetingInsightsPage />
        </AuthProvider>
      );
    }

    // Discord user mappings (admin)
    if (path === '/discord-mappings') {
      return (
        <AuthProvider getAuthHeader={getAuthHeader} user={user}>
          <DiscordMappings />
        </AuthProvider>
      );
    }

    // Shared thread view (read-only, no Dialogue/Recording providers needed)
    if (path.startsWith('/share/')) {
      const token = path.replace('/share/', '');
      return (
        <AuthProvider getAuthHeader={getAuthHeader} user={user}>
          <SharedThreadView token={token} />
        </AuthProvider>
      );
    }

    return (
      <AuthProvider getAuthHeader={getAuthHeader} user={user}>
        <DialogueProvider getAuthHeader={getAuthHeader} user={user}>
          <RecordingProvider getAuthHeader={getAuthHeader} user={user}>
            <ChatWindow user={user} setUser={handleLogout} />
          </RecordingProvider>
        </DialogueProvider>
      </AuthProvider>
    );
  }

  // Show login. The iOS auth modal is still driven by the Google Sign-In
  // widget because it hands the resulting Google ID token to the native
  // bridge — SSO's browser-redirect flow doesn't fit that shape. Everywhere
  // else, log in through the DG SSO integration.
  if (isIosAuthModal) {
    return <GoogleLogin onSuccess={handleLogin} />;
  }
  const returnTo = window.location.pathname + window.location.search;
  return (
    <section className="centered">
      <h2>Please sign in</h2>
      <a
        className="sso-login-btn"
        href={`/api/sso/login?return_to=${encodeURIComponent(returnTo || '/')}`}
      >
        Sign in
      </a>
    </section>
  );
}